<!-- Component that displays a single disposition unit in the disposition view -->
<template>
    <v-card
        class   = "v-flex w-100"
        :class  = "selected || mapSelected ? 'inspection-selected' : ''"
        :ripple = "false"
        @click  = "onSelected"
        flat
        rounded
    >
        <!-- Order color slice -->
        <div
            v-if   = "!location.orderList || location.orderList.length === 0"
            class  = "order-color"
            :style = "orderColor"
        >
        </div>

        <div
            v-for="(ord,index) in location.orderList"
            :key = "index"
            :style = "color (ord)"
        >
        </div>
        <!-- Order color slice -->

        <!-- Order details -->
        <p-disposition-order-details
            v-if  = "showOrder"
            :unit = "location"
        />
        <!-- Order details -->

        <!-- Location header -->
        <div
            v-if   = "location.header"
            :class = "locationHeaderStyle"
            class  = "unit-header unit-header-divider"
        >
            <p-disposition-location
                :location     = "location"
                :dispatch-on  = "dispatchOn"
                :assign-on    = "assignOn"
                :user         = "user"
                :refresh      = "refresh"
                @expand       = "onExpand"
                @collapse     = "onCollapse"
                @dispatch-all = "onDispatchAll"
                @release-all  = "onReleaseAll"
                @assign-all   = "onAssignAll"
                @dissolve-all = "onDissolveAll"
                @locate       = "onLocate"
            />
        </div>
        <!-- Location header -->

        <!-- unit details -->
        <div
            v-if   = "!location.header"
            class  = "ml-0"
            :class = "showOrder ? 'w-50' : 'w-100'"
        >
            <p-disposition-unit-details
                :order       = "order"
                :unit        = "location"
                :assignment  = "assignment"
                :dispatch-on = "dispatchOn"
                :assign-on   = "assignOn"
                :user        = "user"
                @assign      = "onAssign"
                @dissolve    = "onDissolve"
                @dispatch    = "onDispatch"
                @release     = "onRelease"
                @edit        = "onEditUnit"
            />
        </div>
        <!-- unit details -->
    </v-card>
</template>

<script>

import Core                     from "@/modules/core/service/Core";
import PDispositionLocation     from "@/modules/dis/components/order/location/private/PDispositionLocation.vue";
import PDispositionOrderDetails from "@/modules/dis/components/order/location/private/PDispositionOrderDetails.vue";
import PDispositionUnitDetails  from "@/modules/dis/components/order/location/private/PDispositionUnitDetails.vue";
import Order from "@/modules/dis/service/order/Order";

export default {
    name: "PDispositionLocationCard",

    components: {
        PDispositionUnitDetails,
        PDispositionOrderDetails,
        PDispositionLocation,
    },
    props: {
        /**
         * The currently selected order
         */
        order: {

        },

        /**
         * The inspection assignments of the selected order.
         */
        assignments: {
        },

        /**
         * The location represented by the card
         */
        location: {
        },

        /**
         * Determines whether the order should be displayed together with the location
         */
        showOrder: {
            required: false,
            type    : Boolean,
            default : function () {
                return false;
            }
        },

        /**
         * Determines whether the location is selected
         */
        selected: {
            required: false,
            type    : Boolean,
            default : function () {
                return false;
            }
        },

        /**
         * Determines whether the location is selected on the map
         */
        mapSelected: {
            required: false,
            type    : Boolean,
            default : function () {
                return false;
            }
        },

        /**
         * The currently selected user
         */
        user: {

        },

        /**
         * True, if the assign mode is switched on.
         */
        assignOn: {

        },

        /**
         * True, if the dispatch mode is switched on.
         */
        dispatchOn: {

        },
    },

    data: () => ({
        refresh: 0,
    }),

    computed: {
        /**
         * Returns the background color as a CSS style that was defined for an Order. Null, if no color has
         * been specified for the Order.
         *
         * @returns {string|null}
         */
        orderColor () {
            if (this.refresh < 0) {
                return null;
            }

            const width = "min-width:" + (this.showOrder ? "8px;" : "6px;");
            return width + (this.location.order && this.location.order.color
                ? ('background-color:' + this.location.order.color)
                : null)
            ;
        },

        /**
         * Returns the dynamic CSS style for the header. If order information is displayed, the width is 75%,
         * otherwise 100%.
         *
         * @returns {string}
         */
        locationHeaderStyle () {
            return this.showOrder ? ' w-50' : ' w-100';
        },

        /**
         * Returns the inspection information for the unit. If the unit is not yet assigned to an order, null
         * is returned
         *
         * @returns {null|boolean}
         */
        assignment () {
            if (!this.assignments) {
                return null;
            }

            if (this.assignOn) {
                return this.assignments.find(a => a.unitId === this.location.id)
            }

            return this.assignments.find(a => a.unitId === this.location.id && (a.order ? a.order.id : 0) === (this.location.order ? this.location.order.id : 0))
        },
    },

    created() {
        this.$bus.$on(this.$events.dis.assignments, this.onAssignments)
    },

    beforeDestroy() {
        this.$bus.$off(this.$events.dis.assignments, this.onAssignments)
    },

    methods: {
        onClick () {
            this.$emit("click", this.location)
        },

        onExpand () {
            this.$emit("expand", this.location);
        },

        onCollapse () {
            this.$emit("collapse", this.location);
        },

        onSelected () {
            this.$emit("selected", this.location)
        },

        onEditUnit () {
            this.$emit("site", this.location)
        },

        onDissolve () {
            this.$emit("deselect", this.location);
        },

        onAssign () {
            this.$emit("assign", this.location);
        },

        onDispatch () {
            this.$emit("dispatch", this.location);
        },

        onRelease () {
            this.$emit("release", this.location);
        },

        onDispatchAll () {
            this.$emit("dispatch-all", Order.common.toLocation(this.location));
        },

        onReleaseAll () {
            this.$emit("release-all", Order.common.toLocation(this.location));
        },

        onAssignAll () {
            this.$emit("assign-all", Order.common.toLocation(this.location));
        },

        onDissolveAll () {
            this.$emit("dissolve-all", Order.common.toLocation(this.location));
        },

        onLocate (coords) {
            this.$emit("locate", coords);
        },

        onAssignments (event) {
            const modified = event.units.find(unit => unit === this.location.id);
            if (!modified) {
                return;
            }

            this.refresh ++;
        },

        date (value) {
            return Core.format.date.display(value);
        },

        money (value) {
            return Core.format.money(value);
        },

        color (ord) {
            if (this.refresh < 0) {
                return null;
            }

            const width = "border-radius:0;min-width:" + (this.showOrder ? "8px;" : "6px;");
            return width + (ord && ord.color
                ? ('background-color:' + ord.color)
                : null)
                ;
        },
    },
}
</script>

<style scoped>

.order-color {
    min-height: 50%;
    margin-right: 0;
    border-radius: 0!important;
}

.unit-header {
    font-size: 15px;
    font-weight: 400;
    height: 46px;
    padding-left: 0;
    padding-top: 2px;
    width: calc(100% - 6px);
}

/*noinspection ALL*/
.unit-header-divider {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-radius: 0!important;
    border-bottom-color: lightgray;
}


/*noinspection ALL*/
.inspection-selected {
    background-color: aliceblue;
}

</style>